/**
 * Phase 13 Beat 4 — Job Workspace (Executive workbench).
 *
 * Single daily workspace for finding, matching, applying to, and tracking jobs
 * for candidates the Executive is authorized to work with. Replaces the older
 * "Daily Activity" rapid-log page (which is preserved as a permanent redirect).
 *
 * Views (tabs — all query slices of the SAME central Job Library):
 *   - Recommended   : jobs scored for the selected candidate
 *   - Shared        : jobs discovered by other executives that match visible candidates
 *   - My Discoveries: jobs the current user added; default hides no-match discoveries
 *   - Applications  : the actor's application activity (Beat 3 records)
 *
 * All match scoring is delegated to the Beat 2 engine + cache — no new logic.
 */
import { useEffect, useMemo, useState } from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useMe } from "@/lib/authz";
import AddJobDialog from "@/components/AddJobDialog";
import BulkJobImportDialog from "@/components/BulkJobImportDialog";
import ApplicationConfirmDialog from "@/components/ApplicationConfirmDialog";
import { formatPostedAge, formatPostedAbsolute, postedRangeQuery, POSTED_PRESETS, SORT_OPTIONS } from "@/lib/postedDate";
import {
  Sparkle, ShareNetwork, Bookmarks, PaperPlaneTilt, Briefcase, Plus, Books,
  ArrowsClockwise, MagnifyingGlass, Lock, LockOpen, Warning, CheckCircle,
  XCircle, MapPin, Buildings, X, Users, Info,
} from "@phosphor-icons/react";

const TABS = [
  { key: "recommended", label: "Recommended", icon: Sparkle },
  { key: "shared",      label: "Shared",      icon: ShareNetwork },
  { key: "mine",        label: "My Discoveries", icon: Bookmarks },
  { key: "applications", label: "Applications", icon: PaperPlaneTilt },
];

const BAND_TONE = {
  "Highly Relevant": "bg-emerald-600 text-white",
  "Relevant":        "bg-sky-600 text-white",
  "Consider":        "bg-amber-500 text-white",
  "Low Match":       "bg-slate-500 text-white",
};

export default function JobWorkspace() {
  const { hasPerm, canSeeOwner, me } = useMe();
  const [searchParams, setSearchParams] = useSearchParams();

  // Candidate selector
  const [cands, setCands] = useState([]);
  const [candidateId, setCandidateId] = useState(searchParams.get("candidate") || "");
  const [locked, setLocked] = useState(false);

  // Active tab
  const [tab, setTab] = useState(searchParams.get("tab") || "recommended");

  // Filters
  const [minScore, setMinScore] = useState(parseInt(searchParams.get("min") || "50", 10));
  const [postedPreset, setPostedPreset] = useState(searchParams.get("posted") || "all");
  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "match");
  // Phase 13 Beat 4 v2 (Feb 2026) — My Discoveries has two explicit sub-views:
  //   "relevant" (default): jobs I discovered that match at least one visible
  //                          candidate at ≥ 50% (matches Beat 2/3 semantics).
  //   "all":                 every job I discovered, regardless of match.
  // Replaces the previous single-checkbox toggle so the two intents are
  // discoverable and shareable via URL.
  const [mineView, setMineView] = useState(searchParams.get("mine") || "relevant");
  const [search, setSearch] = useState("");

  // Dialogs
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [applyFor, setApplyFor] = useState(null);   // { row, candidateId }
  const [drawerJob, setDrawerJob] = useState(null); // job_id for JD + Who-Else-Fits

  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Applications (separate query for the Applications tab)
  const [apps, setApps] = useState([]);
  // Feb 2026 — per-candidate failures during All-Mode Applications aggregation
  // are captured here so the UI can surface them instead of silently returning
  // empty rows (which used to look identical to a healthy no-data state).
  const [partialErrors, setPartialErrors] = useState([]);

  // Load visible candidates and default the selector to the most-recent one.
  // Role-based scoping (Feb 2026):
  //   Executives / SDE           → `scope=mine`  (only their directly assigned)
  //   Team Leader / Group Lead   → `scope=team`  (own + reports; read-only for reports)
  //   Admin / Super Admin / HR   → no scope     (hierarchy already unrestricted)
  // The Apply button is separately gated to require the candidate be
  // directly assigned to the logged-in user (see `canApply` below).
  useEffect(() => {
    const role = (me?.role || "").toLowerCase();
    const scope = role === "executive" || role === "sde" || role === "senior_executive"
      ? "mine"
      : role === "team_lead" || role === "sdl" || role === "manager"
        ? "team"
        : null;   // admin / super_admin / hr — unrestricted
    const url = scope ? `/candidates?scope=${scope}` : "/candidates";
    api.get(url).then((r) => {
      const list = (r.data || []).filter((c) =>
        (c.service_status === "Active" || c.service_status === "Resumed") &&
        hasPerm("candidate.edit") && canSeeOwner(c.assigned_employee_id));
      setCands(list);
      if (!candidateId) {
        // Default to first visible candidate. "All my candidates" ("") stays explicit.
        const preselect = searchParams.get("candidate");
        if (preselect && list.find((c) => c.id === preselect)) {
          setCandidateId(preselect);
          setLocked(true);
        } else if (list.length > 0) {
          setCandidateId(list[0].id);
        }
      }
    }).catch(() => setCands([]));
    // eslint-disable-next-line
  }, [hasPerm, canSeeOwner, me?.role]);

  // Sync URL params for shareable/bookmark-able state
  useEffect(() => {
    const p = new URLSearchParams();
    if (candidateId) p.set("candidate", candidateId);
    if (tab !== "recommended") p.set("tab", tab);
    if (minScore !== 50) p.set("min", String(minScore));
    if (postedPreset !== "all") p.set("posted", postedPreset);
    if (sortBy !== "match") p.set("sort", sortBy);
    if (tab === "mine" && mineView !== "relevant") p.set("mine", mineView);
    setSearchParams(p, { replace: true });
  }, [candidateId, tab, minScore, postedPreset, sortBy, mineView, setSearchParams]);

  // Fetch tab data
  const load = async () => {
    setBusy(true); setError(null); setPartialErrors([]);
    try {
      if (tab === "applications") {
        // Applications tab: candidate-scoped when selected, else no cross-candidate
        // aggregation endpoint exists; iterate visible candidates from cache.
        if (candidateId) {
          const { data } = await api.get(`/candidates/${candidateId}/applications`);
          setApps(data.rows || []);
        } else {
          // "All My Candidates" — pull for each visible candidate (small N, no
          // new API). Feb 2026 — no longer swallow per-candidate failures;
          // capture them so the UI can show a partial-load warning banner
          // with the specific candidate names + HTTP status that failed.
          const targets = cands.slice(0, 30);
          const results = await Promise.all(targets.map((c) =>
            api.get(`/candidates/${c.id}/applications`)
              .then((r) => ({ ok: true, cand: c, rows: (r.data.rows || []).map((a) => ({ ...a, _candidate_name: c.full_name })) }))
              .catch((ex) => ({
                ok: false,
                cand: c,
                status: ex.response?.status,
                detail: formatApiError(ex.response?.data?.detail) || ex.message || "request failed",
              }))));
          const okRows = results.filter((x) => x.ok).flatMap((x) => x.rows);
          const fails  = results.filter((x) => !x.ok);
          setApps(okRows);
          if (fails.length > 0) {
            setPartialErrors(fails.map((f) => ({
              candidateId: f.cand.id,
              candidateName: f.cand.full_name,
              status: f.status,
              detail: f.detail,
            })));
          }
        }
        setRows([]); setTotal(0);
        return;
      }
      const posted = postedRangeQuery(postedPreset);
      // Phase 13 Beat 4 v2 (Feb 2026) — My Discoveries has two explicit sub-views:
      //   "relevant": min_score=50 + relevant_only=true → jobs I discovered that
      //                match ≥ one visible candidate at ≥ 50%.
      //   "all":     min_score=0  + relevant_only=false → every job I discovered,
      //                regardless of current match (preserves the UAT bug-5 fix).
      // Other tabs keep the user-selected min_score.
      const effectiveMinScore = tab === "mine"
        ? (mineView === "all" ? 0 : 50)
        : minScore;
      const qp = new URLSearchParams({
        filter: tab,
        min_score: String(effectiveMinScore),
        sort_by: sortBy,
        page: "1",
        page_size: "50",
        ...(tab === "mine"
          ? { relevant_only: mineView === "relevant" ? "true" : "false" }
          : {}),
        ...posted,
      });
      const endpoint = candidateId
        ? `/candidates/${candidateId}/job-recommendations?${qp}`
        : `/workspace/recommendations?${qp}`;
      const { data } = await api.get(endpoint);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (ex) {
      // Feb 2026 — surface HTTP status + upstream detail so the UI shows a
      // real error state, not the "No jobs yet" empty state. Keep previous
      // rows so a transient blip doesn't wipe the screen; the error banner
      // is the authoritative signal (empty-state is suppressed while error).
      const status = ex.response?.status;
      const detail = formatApiError(ex.response?.data?.detail);
      const parts = [];
      if (status) parts.push(`HTTP ${status}${ex.response?.statusText ? ` ${ex.response.statusText}` : ""}`);
      parts.push(detail || ex.message || "Failed to load workspace");
      setError(parts.join(" — "));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, candidateId, minScore, postedPreset, sortBy, mineView]);

  const currentCandidate = cands.find((c) => c.id === candidateId);
  // Feb 2026 — Apply is only enabled when the candidate is DIRECTLY assigned
  // to the logged-in user. Team Leader / Group Lead can see their reports'
  // candidates (read-only) but cannot apply on their behalf.
  const isDirectlyMine = !!(currentCandidate && me?.id
                              && currentCandidate.assigned_employee_id === me.id);
  const canApply = !!(currentCandidate && hasPerm("candidate.edit") && isDirectlyMine);

  // Client-side search filter
  const filteredRows = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      (r.job_title || "").toLowerCase().includes(s) ||
      (r.company || "").toLowerCase().includes(s) ||
      (r.location || "").toLowerCase().includes(s));
  }, [rows, search]);

  return (
    <div className="space-y-5" data-testid="job-workspace-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 inline-flex items-center gap-2">
            <Briefcase weight="fill" className="text-indigo-500" /> Job Workspace
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Find, match, and apply — all in one place for your candidates.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <NavLink to="/jobs"
             data-testid="workspace-open-library"
             className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">
            <Books size={13} /> Browse Job Library
          </NavLink>
          <button onClick={() => setBulkOpen(true)}
                   data-testid="workspace-bulk-import"
                   className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">
            <Plus size={13} /> Bulk Import
          </button>
          <button onClick={() => setAddOpen(true)}
                   data-testid="workspace-add-job"
                   className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium">
            <Plus size={13} weight="bold" /> Add Job
          </button>
        </div>
      </div>

      {/* Top bar — candidate selector + lock */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3"
           data-testid="workspace-top-bar">
        <div className="flex-1 min-w-[260px]">
          <label className="text-xs text-slate-500">Candidate {locked && <span className="text-emerald-700">(locked)</span>}</label>
          {locked && currentCandidate ? (
            <div className="border border-emerald-300 bg-emerald-50 rounded-lg px-3 py-2 text-sm font-medium text-emerald-900 flex items-center justify-between"
                 data-testid="workspace-locked-candidate">
              <span>{currentCandidate.full_name} · <span className="text-emerald-700 text-xs">{currentCandidate.email}</span></span>
            </div>
          ) : (
            <select value={candidateId}
                     onChange={(e) => setCandidateId(e.target.value)}
                     data-testid="workspace-candidate-select"
                     className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
              <option value="">All My Candidates</option>
              {cands.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
            </select>
          )}
        </div>
        <button onClick={() => candidateId && setLocked((v) => !v)}
                 data-testid="workspace-lock-toggle"
                 disabled={!candidateId}
                 className={`text-sm px-3 py-2 rounded-lg border inline-flex items-center gap-1 ${
                   locked ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
                          : "border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                 }`}>
          {locked ? <><Lock size={13} /> Locked</> : <><LockOpen size={13} /> Lock candidate</>}
        </button>
        {currentCandidate && (
          <NavLink to={`/candidates/${currentCandidate.id}`}
                    className="text-xs text-indigo-700 hover:underline inline-flex items-center gap-1 mb-2">
            Open full profile ▸
          </NavLink>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="workspace-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                     data-testid={`workspace-tab-${t.key}`}
                     className={`text-sm px-3.5 py-2 rounded-full border inline-flex items-center gap-1.5 transition-colors ${
                       active ? "bg-indigo-600 text-white border-indigo-600"
                              : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                     }`}>
              <Icon size={13} weight={active ? "fill" : "regular"} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* My Discoveries sub-tabs (Phase 13 Beat 4 v2) —
          "Relevant to My Candidates" (default) vs "All My Discoveries" */}
      {tab === "mine" && (
        <div className="flex items-center gap-2" data-testid="workspace-mine-subtabs">
          <span className="text-[11px] uppercase tracking-widest text-slate-500 mr-1">View</span>
          <button onClick={() => setMineView("relevant")}
                   data-testid="workspace-mine-view-relevant"
                   title="Jobs you discovered that match at least one candidate you can see (≥ 50%)"
                   className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                     mineView === "relevant"
                       ? "bg-slate-900 text-white border-slate-900"
                       : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                   }`}>
            Relevant to My Candidates
          </button>
          <button onClick={() => setMineView("all")}
                   data-testid="workspace-mine-view-all"
                   title="Every job you discovered, regardless of match. Newly added jobs always land here."
                   className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                     mineView === "all"
                       ? "bg-slate-900 text-white border-slate-900"
                       : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                   }`}>
            All My Discoveries
          </button>
        </div>
      )}

      {/* Filter row (hidden for Applications tab) */}
      {tab !== "applications" && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-3"
             data-testid="workspace-filters">
          <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-1.5 bg-slate-50 flex-1 min-w-[200px]">
            <MagnifyingGlass size={13} className="text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search title, company, or location…"
                    data-testid="workspace-search"
                    className="flex-1 bg-transparent outline-none text-sm" />
          </div>
          <label className="text-xs text-slate-600 inline-flex items-center gap-1.5">
            Min match
            <select value={minScore} onChange={(e) => setMinScore(parseInt(e.target.value, 10))}
                     data-testid="workspace-min-score"
                     disabled={tab === "mine"}
                     title={tab === "mine" ? "All your discoveries are shown regardless of match; use ‘Relevant to my candidates’ to filter." : ""}
                     className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed">
              <option value={0}>Any</option>
              <option value={50}>50%+</option>
              <option value={70}>70%+</option>
              <option value={85}>85%+</option>
            </select>
          </label>
          <label className="text-xs text-slate-600 inline-flex items-center gap-1.5">
            Sort
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                     data-testid="workspace-sort"
                     className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white">
              {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>

          <div className="w-full flex flex-wrap items-center gap-1.5" data-testid="workspace-posted-chips">
            <span className="text-[11px] text-slate-500">Posted:</span>
            {POSTED_PRESETS.map((p) => (
              <button key={p.key} onClick={() => setPostedPreset(p.key)}
                       data-testid={`workspace-posted-${p.key}`}
                       className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                         postedPreset === p.key
                           ? "bg-slate-900 text-white border-slate-900"
                           : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                       }`}>{p.label}</button>
            ))}
          </div>
        </div>
      )}

      {/* Content pane */}
      {tab !== "applications" && (
        <TabContent
          rows={filteredRows} total={total} busy={busy} error={error}
          tab={tab} candidateId={candidateId} allMode={!candidateId}
          canApply={candidateId ? canApply : false /* All mode requires selecting a candidate to apply */}
          onApply={(r) => setApplyFor({ row: r, candidateId: r.candidate_id || candidateId })}
          onViewJd={(job_id) => setDrawerJob(job_id)}
        />
      )}
      {tab === "applications" && (
        <>
          {error && (
            <div className="border border-red-200 bg-red-50 rounded p-3 text-sm text-red-800 flex items-start gap-3 mb-3"
                 data-testid="workspace-error">
              <XCircle size={16} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">Couldn't load applications</div>
                <div className="text-xs mt-0.5 break-words">{error}</div>
              </div>
              <button onClick={load} disabled={busy}
                       data-testid="workspace-error-retry"
                       className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded border border-red-300 bg-white text-red-700 hover:bg-red-100 disabled:opacity-50 shrink-0">
                <ArrowsClockwise size={11} /> Retry
              </button>
            </div>
          )}
          {partialErrors.length > 0 && (
            <div className="border border-amber-200 bg-amber-50 rounded p-3 text-sm text-amber-900 flex items-start gap-3 mb-3"
                 data-testid="workspace-partial-error">
              <Warning size={16} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">
                  Couldn't load applications for {partialErrors.length} of your candidates
                </div>
                <ul className="text-xs mt-1 list-disc pl-5 space-y-0.5">
                  {partialErrors.slice(0, 5).map((f) => (
                    <li key={f.candidateId} data-testid={`workspace-partial-error-${f.candidateId}`}>
                      <span className="font-medium">{f.candidateName}</span>
                      {f.status ? ` — HTTP ${f.status}` : ""}
                      {f.detail ? ` — ${f.detail}` : ""}
                    </li>
                  ))}
                  {partialErrors.length > 5 && (
                    <li className="italic">…and {partialErrors.length - 5} more</li>
                  )}
                </ul>
              </div>
              <button onClick={load} disabled={busy}
                       data-testid="workspace-partial-error-retry"
                       className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 disabled:opacity-50 shrink-0">
                <ArrowsClockwise size={11} /> Retry
              </button>
            </div>
          )}
          {!error && (
            <ApplicationsList rows={apps} allMode={!candidateId} onViewJd={(app_id) => setDrawerJob(null) /* JD drawer for applications uses its own opener */} />
          )}
        </>
      )}

      {/* Dialogs */}
      {addOpen && (
        <AddJobDialog onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); load(); }} />
      )}
      {bulkOpen && (
        <BulkJobImportDialog onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />
      )}
      {applyFor && (
        <ApplicationConfirmDialog
          candidateId={applyFor.candidateId}
          job={applyFor.row}
          onClose={() => setApplyFor(null)}
          onSubmitted={() => { setApplyFor(null); load(); }} />
      )}
      {drawerJob && (
        <JobDetailDrawer jobId={drawerJob} candidateId={candidateId}
                          onClose={() => setDrawerJob(null)}
                          onApply={(job) => setApplyFor({ row: job, candidateId })} />
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Job / recommendation list — used by Recommended, Shared, My Discoveries
 * -------------------------------------------------------------------------- */
function TabContent({ rows, total, busy, error, tab, allMode, canApply, onApply, onViewJd }) {
  if (busy && rows.length === 0) {
    return (
      <div className="grid gap-3" data-testid="workspace-loading">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 h-24 animate-pulse" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="border border-red-200 bg-red-50 rounded p-3 text-sm text-red-800 flex items-start gap-3"
           data-testid="workspace-error">
        <XCircle size={16} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-medium">Couldn't load this workspace view</div>
          <div className="text-xs mt-0.5 break-words">{error}</div>
        </div>
      </div>
    );
  }
  if (rows.length === 0) {
    const emptyMsg = tab === "shared"
      ? "No shared jobs match your candidate(s) yet. Encourage teammates to add jobs to the central Library."
      : tab === "mine"
        ? "You haven't added any jobs yet — or none are relevant to your candidates. Every job you add lands in the central Library."
        : "No recommendations at the current threshold. Lower the match threshold or Add a Job.";
    return (
      <div className="border border-dashed border-slate-200 rounded-lg p-8 text-center text-sm text-slate-500"
           data-testid={`workspace-empty-${tab}`}>{emptyMsg}</div>
    );
  }
  return (
    <div className="space-y-3" data-testid="workspace-rows">
      {rows.map((r) => (
        <JobRow key={`${r.job_id}-${r.candidate_id || ""}`}
                 row={r} allMode={allMode} canApply={canApply}
                 onApply={() => onApply(r)}
                 onViewJd={() => onViewJd(r.job_id)} />
      ))}
      <div className="text-[11px] text-slate-500 text-right pt-1" data-testid="workspace-count">
        {rows.length} of {total}
      </div>
    </div>
  );
}

function JobRow({ row, allMode, canApply, onApply, onViewJd }) {
  const bandTone = BAND_TONE[row.band] || "bg-slate-500 text-white";
  const applied = row.already_applied;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:border-indigo-300 transition-colors"
         data-testid={`workspace-row-${row.job_id}${allMode ? "-" + row.candidate_id : ""}`}>
      <div className="flex items-start gap-4">
        <div className="relative w-14 h-14 shrink-0">
          <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke="#e2e8f0" strokeWidth="4" />
            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke="#4f46e5" strokeWidth="4"
                  strokeDasharray={`${row.overall_score}, 100`} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-slate-900">
            {row.overall_score}%
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-slate-900" data-testid={`workspace-row-title-${row.job_id}`}>
              {row.job_title || "Untitled"}
            </h4>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${bandTone}`}>
              {row.band}
            </span>
            {row.is_shared && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 inline-flex items-center gap-1"
                    data-testid={`workspace-shared-badge-${row.job_id}`}>
                <ShareNetwork size={10} weight="fill" /> Shared Job
              </span>
            )}
            {row.review_required && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                <Warning size={10} weight="fill" /> Review Required
              </span>
            )}
            {applied && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-700 inline-flex items-center gap-1">
                <CheckCircle size={10} weight="fill" /> Already {row.latest_application_status}
              </span>
            )}
          </div>

          <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-3">
            {allMode && row.candidate_name && (
              <span className="inline-flex items-center gap-1 text-indigo-700 font-medium">
                ▸ For: {row.candidate_name}
              </span>
            )}
            <span className="inline-flex items-center gap-1"><Buildings size={10} /> {row.company || "—"}</span>
            <span className="inline-flex items-center gap-1"><MapPin size={10} /> {row.location || "—"}</span>
            {row.work_arrangement && <span>· {row.work_arrangement}</span>}
            <span title={formatPostedAbsolute(row.originally_posted_at) || ""}
                   data-testid={`workspace-posted-${row.job_id}`}
                   className="text-slate-500">
              · {formatPostedAge(row.originally_posted_at)}
            </span>
            {row.source && <span>· {row.source}</span>}
            {row.is_shared && row.discovered_by_name && (
              <span className="text-slate-400">· by {row.discovered_by_name}</span>
            )}
          </div>

          {row.top_matched && row.top_matched.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {row.top_matched.map((m) => (
                <span key={`${row.job_id}-m-${m}`} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1">
                  <CheckCircle size={9} weight="fill" /> {m}
                </span>
              ))}
            </div>
          )}
          {row.top_gaps && row.top_gaps.length > 0 && (
            <div className="mt-1.5 space-y-0.5">
              {row.top_gaps.slice(0, 2).map((g) => (
                <div key={`${row.job_id}-g-${g}`} className="text-[11px] text-amber-700 inline-flex items-center gap-1">
                  <XCircle size={10} weight="fill" /> {g}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 items-end shrink-0">
          <button onClick={onViewJd}
                   data-testid={`workspace-view-jd-${row.job_id}`}
                   className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50">
            <Info size={11} /> View JD
          </button>
          <button onClick={onApply} disabled={!canApply || applied}
                   data-testid={`workspace-apply-${row.job_id}`}
                   className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed">
            <PaperPlaneTilt size={11} weight="fill" />
            {applied ? "Applied" : allMode && !row.candidate_id ? "Select candidate" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Applications list — Beat 3 records only
 * -------------------------------------------------------------------------- */
function ApplicationsList({ rows, allMode }) {
  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-slate-200 rounded-lg p-8 text-center text-sm text-slate-500"
           data-testid="workspace-applications-empty">
        No applications yet. Use the Recommended tab to apply on a candidate's behalf.
      </div>
    );
  }
  const viewJD = async (aid) => {
    try {
      const { data } = await api.get(`/applications/${aid}/jd`);
      const win = window.open("", "_blank", "noopener,noreferrer");
      if (win) {
        // Use DOM APIs + textContent (not document.write) so the JD text is
        // rendered as plain text and never parsed as HTML — safe even if a
        // future JD scrape ever injects markup.
        win.document.title = "JD snapshot";
        const pre = win.document.createElement("pre");
        Object.assign(pre.style, {
          whiteSpace: "pre-wrap", fontFamily: "sans-serif",
          padding: "24px", maxWidth: "800px", margin: "auto", lineHeight: "1.5",
        });
        pre.textContent = data.snapshot.jd_text || "";
        win.document.body.appendChild(pre);
      }
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "JD snapshot unavailable"); }
  };
  const statusTone = { started: "bg-amber-50 text-amber-800 border-amber-200",
                        submitted: "bg-emerald-50 text-emerald-800 border-emerald-200",
                        cancelled: "bg-slate-100 text-slate-600 border-slate-200" };
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden"
         data-testid="workspace-applications">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
          <tr>
            {allMode && <th className="text-left px-4 py-2">Candidate</th>}
            <th className="text-left px-4 py-2">Job</th>
            <th className="text-left px-4 py-2">Match</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-left px-4 py-2">Submitted</th>
            <th className="text-left px-4 py-2">Submitted By</th>
            <th className="text-right px-4 py-2">JD</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((a) => (
            <tr key={a.id} data-testid={`workspace-app-row-${a.id}`}>
              {allMode && <td className="px-4 py-2 text-slate-800">{a._candidate_name || a.candidate_name || "—"}</td>}
              <td className="px-4 py-2">
                <div className="font-medium text-slate-800">{a.job_title || "—"}</div>
                <div className="text-[11px] text-slate-500">{a.company || "—"} · {a.location || "—"}</div>
              </td>
              <td className="px-4 py-2">
                <div className="font-semibold text-slate-900">{(a.match_snapshot || {}).overall_score ?? "—"}%</div>
                <div className="text-[10px] text-slate-500">{(a.match_snapshot || {}).band || ""}</div>
              </td>
              <td className="px-4 py-2">
                <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${statusTone[a.status] || statusTone.started}`}>
                  {a.status || "started"}
                </span>
              </td>
              <td className="px-4 py-2 text-xs text-slate-600">
                {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : a.started_at ? new Date(a.started_at).toLocaleString() : "—"}
              </td>
              <td className="px-4 py-2 text-xs text-slate-600">{a.submitted_by_name || "—"}</td>
              <td className="px-4 py-2 text-right">
                <button onClick={() => viewJD(a.id)}
                         data-testid={`workspace-app-view-jd-${a.id}`}
                         className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded border border-slate-200 hover:bg-slate-50">
                  View JD
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Job Detail side-drawer with "Who Else Fits This Job?"
 * -------------------------------------------------------------------------- */
function JobDetailDrawer({ jobId, candidateId, onClose, onApply }) {
  const [job, setJob] = useState(null);
  const [jd, setJd] = useState(null);
  const [match, setMatch] = useState(null);
  const [others, setOthers] = useState([]);
  const [busy, setBusy] = useState(true);
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const [jRes, jdRes] = await Promise.all([
          api.get(`/jobs/${jobId}`),
          api.get(`/jobs/${jobId}/jd`).catch(() => ({ data: null })),
        ]);
        setJob(jRes.data);
        setJd(jdRes.data?.snapshot);
        if (candidateId) {
          const [mRes, oRes] = await Promise.all([
            api.get(`/matches/pair?candidate_id=${candidateId}&job_id=${jobId}`).catch(() => ({ data: null })),
            api.get(`/jobs/${jobId}/matching-candidates?min_score=50&limit=20`).catch(() => ({ data: { rows: [] } })),
          ]);
          setMatch(mRes.data);
          setOthers(oRes.data.rows || []);
        } else {
          const oRes = await api.get(`/jobs/${jobId}/matching-candidates?min_score=50&limit=20`).catch(() => ({ data: { rows: [] } }));
          setOthers(oRes.data.rows || []);
        }
        // History of applications for THIS job
        // Uses candidate iteration (no aggregate endpoint) — kept small.
        // Skipped for brevity in the drawer; visible on the candidate side.
        setApplications([]);
      } catch { /* handled via null states */ }
      finally { setBusy(false); }
    })();
  }, [jobId, candidateId]);

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm flex justify-end"
         data-testid="workspace-jd-drawer" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white h-full overflow-auto shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-100 flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-slate-900" data-testid="jd-drawer-title">{job?.job_title || "…"}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {job?.company || "—"} · {job?.location || "—"}
              {job?.work_arrangement && <> · {job.work_arrangement}</>}
              <span title={formatPostedAbsolute(job?.originally_posted_at) || ""}
                     className="ml-2 text-slate-400" data-testid="jd-drawer-posted">
                · {formatPostedAge(job?.originally_posted_at)}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {candidateId && job && (
              <button onClick={() => onApply(job && { ...job, job_id: job.id, band: match?.band, overall_score: match?.overall_score, review_required: match?.review_required, review_reasons: match?.review_reasons })}
                       data-testid="jd-drawer-apply"
                       className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700">
                Apply ▸
              </button>
            )}
            <button onClick={onClose} data-testid="jd-drawer-close"
                     className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
          </div>
        </div>

        {busy && <div className="p-5 text-sm text-slate-500">Loading…</div>}

        {!busy && job && (
          <div className="p-5 space-y-5">
            <section className="border border-slate-200 rounded-lg p-4">
              <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-2">Job Information</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><b>Source</b> · {job.source || "—"}</div>
                <div><b>Employment</b> · {job.employment_type || "—"}</div>
                <div><b>Salary</b> · {job.salary_min ? `$${job.salary_min}–$${job.salary_max || "—"}` : "—"}</div>
                <div><b>Experience</b> · {job.required_experience_years ?? "—"} yrs</div>
                <div><b>Education</b> · {job.education || "—"}</div>
                <div><b>Reused</b> · {job.reuse_count ?? 0}×</div>
                <div className="col-span-2"><b>Skills</b> · {(job.required_skills || []).join(", ") || "—"}</div>
              </div>
              {jd && (
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-indigo-700">View JD snapshot</summary>
                  <pre className="whitespace-pre-wrap mt-2 p-3 bg-slate-50 rounded text-slate-700 text-[11px] max-h-64 overflow-auto">
                    {jd.jd_text}
                  </pre>
                </details>
              )}
            </section>

            {match && (
              <section className="border border-slate-200 rounded-lg p-4"
                       data-testid="jd-drawer-match">
                <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-2">Candidate Match</h4>
                <div className="flex items-center gap-3">
                  <div className="text-3xl font-bold text-slate-900">{match.overall_score}%</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${BAND_TONE[match.band] || "bg-slate-500 text-white"}`}>
                    {match.band}
                  </span>
                </div>
                {match.review_required && (
                  <div className="mt-2 text-[11px] text-amber-800">
                    <Warning size={10} weight="fill" className="inline-block mr-1" />
                    Review Required: {(match.review_reasons || []).join("; ")}
                  </div>
                )}
                <div className="mt-3 grid grid-cols-2 gap-1.5">
                  {Object.entries(match.factors || {}).map(([k, f]) => (
                    <div key={k} className="text-[11px] flex items-center justify-between border border-slate-100 rounded px-2 py-1">
                      <span className="capitalize text-slate-600">{k}</span>
                      <span className="font-mono">
                        {f.score != null ? `${f.score}%` : "—"}
                        <span className="text-slate-400"> · {f.status}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="border border-slate-200 rounded-lg p-4"
                     data-testid="jd-drawer-who-else-fits">
              <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-2 inline-flex items-center gap-1">
                <Users size={12} /> Who Else Fits This Job?
              </h4>
              <p className="text-[11px] text-slate-500 mb-2">
                Showing candidates you're authorized to see. Contact info and salary are never exposed here.
              </p>
              {others.length === 0 ? (
                <div className="text-xs text-slate-400 italic py-2">No matching candidates at 50%+ within your visibility.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="text-left py-1.5">Candidate</th>
                      <th className="text-left py-1.5">Role</th>
                      <th className="text-left py-1.5">Location</th>
                      <th className="text-left py-1.5">Match</th>
                      <th className="text-left py-1.5">Status</th>
                      <th className="py-1.5"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {others.map((o) => (
                      <tr key={o.candidate_id} data-testid={`who-else-row-${o.candidate_id}`}>
                        <td className="py-1.5">
                          {o.candidate_name}
                          {o.is_mine && <span className="ml-1 text-[9px] text-indigo-700 font-semibold">MINE</span>}
                        </td>
                        <td className="py-1.5 text-slate-600">{o.preferred_role || "—"}</td>
                        <td className="py-1.5 text-slate-600">{o.location || "—"}</td>
                        <td className="py-1.5 font-semibold">{o.overall_score}%</td>
                        <td className="py-1.5 text-slate-600">{o.service_status}</td>
                        <td className="py-1.5 text-right">
                          <NavLink to={`/candidates/${o.candidate_id}`}
                                    className="text-indigo-700 hover:underline">Open</NavLink>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
